import type { CustomerPaymentMethodStatus, DashboardCustomer } from '@models/customers';
import type { BrandCode } from '@models/payments';
import { apiClient } from '@api/client';

export type GetCustomersParams = {
  page: number;
  limit: number;
  fromDate?: string;
  toDate?: string;
  searchQuery?: string;
  customerId?: string;
  brandCode?: BrandCode;
  hasDebt?: boolean;
  hasActiveSubscription?: boolean;
  testUser?: boolean;
};

export type CustomersResponse = {
  data: DashboardCustomer[];
  hasNextPage: boolean;
};
export type ExecuteScheduledPaymentParams = {
  customerId: string;
  scheduledPaymentId: string;
};

export type ExecuteScheduledPaymentResponse = {
  scheduledPaymentId: string;
  enqueuedForExecution: boolean;
};

export async function getCustomers(params: GetCustomersParams): Promise<CustomersResponse> {
  const { data } = await apiClient.get<CustomersResponse>('/dashboard-customers', { params });
  return data;
}

// Marks the customer's active debts FAILED and cancels upcoming debt-collection scheduled payments.
export async function cancelCustomerDebts(customerId: string): Promise<unknown> {
  const { data } = await apiClient.post(
    `/debt-collection/customers/${encodeURIComponent(customerId)}/cancel`,
  );
  return data;
}

export async function executeScheduledPayment(
  params: ExecuteScheduledPaymentParams,
): Promise<ExecuteScheduledPaymentResponse> {
  const { data } = await apiClient.post<ExecuteScheduledPaymentResponse>(
    `/scheduled-payments/customers/${encodeURIComponent(params.customerId)}/execute-payment`,
    { scheduledPaymentId: params.scheduledPaymentId },
  );
  return data;
}

export type UpdateCustomerPaymentMethodParams = {
  customerPaymentMethodId: string;
  /** ISO timestamp; recurring charges are blocked on this method until it passes. */
  preventRecurringPaymentsTill?: string;
  status?: CustomerPaymentMethodStatus;
};

export type UpdateCustomerPaymentMethodResponse = {
  id: string;
  status: CustomerPaymentMethodStatus;
  preventRecurringPaymentsTill: string | null;
  updatedAt: string;
};

// Partial update — only the provided fields are written on the payment method.
export async function updateCustomerPaymentMethod({
  customerPaymentMethodId,
  ...payload
}: UpdateCustomerPaymentMethodParams): Promise<UpdateCustomerPaymentMethodResponse> {
  const { data } = await apiClient.patch<UpdateCustomerPaymentMethodResponse>(
    `/customer-payment-methods/${encodeURIComponent(customerPaymentMethodId)}`,
    payload,
  );
  return data;
}
